import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import { fileChecks, IMPORT_BATCH, type ImportRow } from "../domain/customerImportCsv";

export interface ImportResult {
  line: number;
  status: "ok" | "imported" | "refused" | "failed";
  reason?: string;
  mobile_missing?: boolean;
  mobile_shared?: boolean;
  needs_password?: boolean;
}

export interface ImportOutcome {
  results: ImportResult[];
  good: number;
  activeBefore: number;
  stopped?: { after: number; reason: string };
}

/**
 * Sends the rows in batches. dryRun writes nothing, and counts what would go in.
 * A line repeating a login is refused here and never sent. If a batch fails, what
 * was done so far is kept and `stopped` says why: upload again, and customers
 * already imported are refused, not repeated.
 */
export async function runCustomerImport(i: CallerIdentity, ns: string, rows: ImportRow[], dryRun: boolean, onProgress?: (done: number) => void): Promise<ImportOutcome> {
  const { repeated, sharedMobile } = fileChecks(rows);
  const send = rows.filter((r) => !repeated.has(r.line));
  const out: ImportOutcome = { results: [...repeated].map(([line, reason]) => ({ line, status: "refused", reason })), good: 0, activeBefore: 0 };
  for (let at = 0; at < send.length; at += IMPORT_BATCH) {
    try {
      const r = await callGateway<{ results: ImportResult[]; good: number; activebefore: number }>(
        i, config().gatewayBase, `/operator/${encodeURIComponent(ns)}/import/customers${dryRun ? "?dry_run=true" : ""}`,
        { method: "POST", body: { rows: send.slice(at, at + IMPORT_BATCH) } },
      );
      if (at === 0) out.activeBefore = r.data.activebefore;
      out.results.push(...r.data.results.map((x) => (sharedMobile.has(x.line) ? { ...x, mobile_shared: true } : x)));
      out.good += r.data.good;
    } catch (e) {
      out.stopped = { after: at, reason: e instanceof Error ? e.message : "The connection failed." };
      break;
    }
    onProgress?.(Math.min(at + IMPORT_BATCH, send.length));
  }
  out.results.sort((a, b) => a.line - b.line);
  return out;
}
